/* import __COLOCATED_TEMPLATE__ from './dynamic-content-editor.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type DynamicContent from 'embercom/models/articles/dynamic-content';
import DynamicContentComposerConfig from 'embercom/lib/articles/dynamic-content-composer-config';
import { BlocksDocument } from '@intercom/composer-ember';
import type { Block, BlockList } from '@intercom/interblocks.ts';
import { post, put } from 'embercom/lib/ajax';

interface Variation {
  locale: string;
  textContent: string;
  jsonBlocks: Block[];
}

interface Args {
  model: DynamicContent;
  onSaveSuccess?: () => void;
  onCancel?: () => void;
  onReady?: (actions: { save: () => Promise<void> }) => void;
}

interface Signature {
  Args: Args;
}

export default class DynamicContentEditor extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: $TSFixMe;
  @service declare helpCenterService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;

  @tracked variations: Variation[] = [];
  @tracked activeLocaleIndex = 0;
  @tracked isSaving = false;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.initVariations();
    this.args.onReady?.({ save: this.save });
  }

  private initVariations() {
    let model = this.args.model;
    let modelVariations = model.variations;
    if (modelVariations) {
      let items: Variation[] = [];
      for (let i = 0; i < Number(modelVariations.length); i++) {
        let v = modelVariations.objectAt(i) as any;
        if (v) {
          items.push({
            locale: v.get ? v.get('locale') : (v.locale ?? ''),
            textContent: v.get ? v.get('textContent') : (v.textContent ?? ''),
            jsonBlocks: v.get ? v.get('jsonBlocks') : (v.jsonBlocks ?? []),
          });
        }
      }
      this.variations = items;
    }
  }

  get isNew(): boolean {
    return this.args.model.get('isNew');
  }

  get isNameValid(): boolean {
    let len = this.args.model.name?.length ?? 0;
    return len > 0 && len <= 255;
  }

  get composerConfig() {
    let analytics = {
      trackAnalyticsEvent: () => {},
    };
    return new DynamicContentComposerConfig(this.appService.app, 'ltr', analytics);
  }

  get activeVariationBlocksDoc(): BlocksDocument | undefined {
    let variation = this.variations[this.activeLocaleIndex];
    if (!variation) {
      return undefined;
    }
    let blocks = variation.jsonBlocks ?? [];
    return new BlocksDocument(blocks as BlockList);
  }

  get title() {
    return this.isNew
      ? this.intl.t('knowledge-hub.dynamic-contents.editor.create-title')
      : this.intl.t('knowledge-hub.dynamic-contents.editor.edit-title');
  }

  get contentTypeOptions() {
    return [
      {
        value: 'block',
        text: this.intl.t('knowledge-hub.dynamic-contents.editor.content-type-block'),
      },
      {
        value: 'inline',
        text: this.intl.t('knowledge-hub.dynamic-contents.editor.content-type-inline'),
      },
    ];
  }

  get availableLocales(): Array<{ text: string; value: string }> {
    let allLocales = this.helpCenterService.allUniqueLocalesOrdered ?? [];
    let usedLocales = new Set(this.variations.map((v) => v.locale));
    return allLocales
      .filter((locale: { localeId: string }) => !usedLocales.has(locale.localeId))
      .map((locale: { name: string; localeId: string }) => ({
        text: `${locale.name} (${locale.localeId})`,
        value: locale.localeId,
      }));
  }

  @action
  localeDisplayName(localeId: string): string {
    let allLocales = this.helpCenterService.allUniqueLocalesOrdered ?? [];
    let found = allLocales.find((l: { localeId: string }) => l.localeId === localeId);
    return found ? `${found.name} (${localeId})` : localeId;
  }

  @action
  updateName(value: string) {
    this.args.model.name = value;
  }

  @action
  updateContentType(item: any) {
    let value = typeof item === 'string' ? item : item.value;
    this.args.model.contentType = value;
  }

  @action
  setActiveLocale(index: number) {
    this.activeLocaleIndex = index;
  }

  @action
  addVariation(item: any) {
    let locale = typeof item === 'string' ? item : item.value;
    this.variations = [...this.variations, { locale, textContent: '', jsonBlocks: [] }];
    this.activeLocaleIndex = this.variations.length - 1;
  }

  @action
  updateVariationTextContent(index: number, event: Event) {
    let variation = this.variations[index];
    if (variation) {
      let updated = { ...variation, textContent: (event.target as HTMLInputElement).value };
      let newVariations = [...this.variations];
      newVariations[index] = updated;
      this.variations = newVariations;
    }
  }

  @action
  composerChanged(blocksDoc: BlocksDocument) {
    let variation = this.variations[this.activeLocaleIndex];
    if (variation) {
      let updated = { ...variation, jsonBlocks: blocksDoc.blocks as Block[] };
      let newVariations = [...this.variations];
      newVariations[this.activeLocaleIndex] = updated;
      this.variations = newVariations;
    }
  }

  @action
  removeVariation(index: number) {
    let newVariations = [...this.variations];
    newVariations.splice(index, 1);
    this.variations = newVariations;
    if (this.activeLocaleIndex >= this.variations.length) {
      this.activeLocaleIndex = Math.max(0, this.variations.length - 1);
    }
  }

  private buildPayload() {
    let model = this.args.model;
    let variations = this.variations.map((v) => {
      let variation: Record<string, unknown> = { locale: v.locale };
      if (model.contentType === 'inline') {
        variation.text_content = v.textContent;
      } else {
        variation.json_blocks = v.jsonBlocks ?? [];
      }
      return variation;
    });
    return {
      app_id: this.appService.app.id,
      name: model.name,
      content_type: model.contentType,
      default_locale: model.defaultLocale,
      variations,
    };
  }

  @action
  async save() {
    if (!this.isNameValid) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.dynamic-contents.editor.name-validation-error'),
      );
      return;
    }
    this.isSaving = true;
    try {
      let payload = this.buildPayload();
      let wasNew = this.isNew;
      if (wasNew) {
        await post('/ember/articles/dynamic_contents', payload);
        this.args.model.deleteRecord();
      } else {
        await put(`/ember/articles/dynamic_contents/${this.args.model.id}`, payload);
      }
      this.notificationsService.notifyConfirmation(
        wasNew
          ? this.intl.t('knowledge-hub.dynamic-contents.editor.save-success-create')
          : this.intl.t('knowledge-hub.dynamic-contents.editor.save-success-update'),
      );
      this.args.onSaveSuccess?.();
    } catch {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.dynamic-contents.editor.save-error'),
      );
    } finally {
      this.isSaving = false;
    }
  }

  @action
  cancel() {
    if (this.isNew) {
      this.args.model.deleteRecord();
    } else {
      this.args.model.rollbackAttributes();
    }
    this.args.onCancel?.();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::DynamicContents::DynamicContentEditor': typeof DynamicContentEditor;
    'knowledge-hub/dynamic-contents/dynamic-content-editor': typeof DynamicContentEditor;
  }
}
